import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";

import {
  useCategoryOptions,
  useSubcategoryOptions,
  usePremise,
  useConsumerStatus,
} from "@features/cx/consumerManagement/hooks";
import { useOwnerTypeChoices } from "@features/cx/disconnect/hooks";
import { useSearchConsumersInfo } from "@features/cx/reconnect/hooks";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { Badge } from "@shared/ui/badge";

interface SearchResult {
  id: string | number;
  consumerNo: string;
  firstName: string;
  lastName: string;
  zone?: string;
  statusDisplay: string;
  contactNumber: string;
  email: string;
  status?: string;
}

interface ManualSelectionTabProps {
  onAddConsumers: (consumers: any[]) => void;
  selectedIds: string[];
  onToggleConsumer: (consumer: SearchResult, isSelected: boolean) => void;
  remoteUtilityId: number;
}

const ManualSelectionTab: React.FC<ManualSelectionTabProps> = ({
  onAddConsumers,
  selectedIds,
  onToggleConsumer,
  remoteUtilityId,
}) => {
  const [filters, setFilters] = useState({
    page: 1,
    search_data: "",
    pageSize: PAGE_SIZE,
    category: "",
    subcategory: "",
    premise: "",
    accountType: "",
    status: "",
  });

  const filterForm = useForm({
    defaultValues: {
      premise: "",
      accountType: "",
      status: "",
      category: "",
      subcategory: "",
    },
  });

  const watchCategory = filterForm.watch("category");

  // --- Fetch Choices ---
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategoryOptions(Number(remoteUtilityId));

  const categoryOptions = useMemo(() => {
    return (
      categoriesData?.result?.map((cat: any) => ({
        label: cat.name,
        value: cat.code,
      })) || []
    );
  }, [categoriesData]);

  const { data: subCategoriesData, isLoading: isLoadingSubCategories } =
    useSubcategoryOptions(Number(remoteUtilityId), watchCategory);

  const subCategoryOptions = useMemo(() => {
    return (
      subCategoriesData?.result?.map((sub: any) => ({
        label: sub.name,
        value: sub.code,
      })) || []
    );
  }, [subCategoriesData]);

  const { data: premisesData, isLoading: isLoadingPremises } = usePremise({
    remote_utility_id: Number(remoteUtilityId),
    config_level: "premise",
  });

  const premiseOptions = useMemo(() => {
    return (
      premisesData?.result?.map((premise: any) => ({
        label: premise.name,
        value: premise.code,
      })) || []
    );
  }, [premisesData]);

  const { data: ownerTypesData } = useOwnerTypeChoices();
  const ownerTypeOptions = useMemo(() => {
    return (
      ownerTypesData?.result?.map((item: any) => ({
        label: item.value,
        value: item.key,
      })) || []
    );
  }, [ownerTypesData]);

  const { data: statusesData, isLoading: isLoadingStatuses } =
    useConsumerStatus(remoteUtilityId.toString());

  const statusOptions = useMemo(() => {
    return (
      statusesData?.map((status: any) => ({
        label: status.value,
        value: status.key,
      })) || []
    );
  }, [statusesData]);

  const { data: searchConsumersData, isFetching: isLoading } =
    useSearchConsumersInfo({
      remoteUtilityId: Number(remoteUtilityId),
      requestType: "Reconnection",
      page: filters.page,
      limit: filters.pageSize,
      searchData: filters.search_data,
      category: filters.category,
      subCategory: filters.subcategory,
      premise: filters.premise,
      status: filters.status,
      accountType: filters.accountType,
    });

  const searchResults = searchConsumersData?.results || [];
  const totalItems = searchConsumersData?.count || 0;

  const handleSearch = (term: string) => {
    setFilters((prev) => ({ ...prev, search_data: term, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const pagination = useMemo(
    () => ({
      currentPage: filters.page,
      totalPages: Math.ceil(totalItems / filters.pageSize),
      totalItems,
      itemsPerPage: filters.pageSize,
      startItem: (filters.page - 1) * filters.pageSize + 1,
      endItem: Math.min(filters.page * filters.pageSize, totalItems),
    }),
    [filters.page, filters.pageSize, totalItems],
  );

  const columns: TableColumn<SearchResult>[] = [
    { header: "Account No", key: "consumerNo" },
    {
      header: "Name",
      key: "firstName",
      render: (value: any, row) => {
        return value + " " + (row?.lastName || "");
      },
    },
    {
      header: "Phone",
      key: "contactNumber",
    },
    {
      header: "Email",
      key: "email",
    },
    {
      header: "Category",
      key: "categoryDisplay",
    },
    {
      header: "Sub Category",
      key: "subCategoryDisplay",
    },
    {
      header: "Premise",
      key: "territoryData",
      render: (val: any, row: any) => {
        return <span>{val?.service?.premise || "N/A"}</span>;
      },
    },
    {
      header: "Account Type",
      key: "ownershipDisplay",
    },
    {
      header: "Status",
      key: "statusDisplay",
      render: (value: any) => {
        return <Badge className={getStatusBadgeClass(value)}>{value}</Badge>;
      },
    },
  ];

  const filterFields: FormField[] = [
    {
      name: "category",
      label: "Category",
      type: "select",
      placeholder: isLoadingCategories ? "Loading..." : "Select Category",
      options: categoryOptions,
    },
    {
      name: "subcategory",
      label: "Sub Category",
      type: "select",
      placeholder: !watchCategory
        ? "Select Category first"
        : isLoadingSubCategories
          ? "Loading..."
          : "Select Sub Category",
      options: subCategoryOptions,
      disabled: !watchCategory,
    },
    {
      name: "premise",
      label: "Premise",
      type: "select",
      placeholder: isLoadingPremises ? "Loading..." : "Select Premise",
      options: premiseOptions,
    },
    {
      name: "accountType",
      label: "Account Type",
      type: "select",
      placeholder: "Select Account Type",
      options: ownerTypeOptions,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      placeholder: isLoadingStatuses ? "Loading..." : "Select Status",
      options: statusOptions,
    },
  ];

  const handleSelectionChange = (newSelectedIds: string[]) => {
    const currentIds = new Set(selectedIds);
    const updatedIds = new Set(newSelectedIds);

    // Finding what was added
    newSelectedIds.forEach((id) => {
      if (!currentIds.has(id)) {
        const consumer = searchResults.find(
          (r: any) => String(r.id) === String(id),
        );
        if (consumer) onToggleConsumer(consumer, true);
      }
    });

    // Finding what was removed
    selectedIds.forEach((id) => {
      if (!updatedIds.has(id)) {
        const consumer = searchResults.find(
          (r: any) => String(r.id) === String(id),
        );
        if (consumer) onToggleConsumer(consumer, false);
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm">
        <GenericTable<SearchResult>
          data={searchResults}
          columns={columns}
          rowKey="id"
          selectable={true}
          selectedRows={selectedIds}
          onSelectionChange={handleSelectionChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by Account ID or Name..."
          filterPanel={
            <div className="p-4 bg-neutral-50/50 rounded-lg border border-neutral-100">
              <DynamicForm
                form={filterForm}
                fields={filterFields}
                gridColumns={3}
                onSubmit={(data) => {
                  setFilters((prev) => ({
                    ...prev,
                    ...data,
                    page: 1,
                  }));
                }}
                onReset={() => {
                  filterForm.reset();
                  setFilters({
                    page: 1,
                    search_data: "",
                    pageSize: PAGE_SIZE,
                    category: "",
                    subcategory: "",
                    premise: "",
                    accountType: "",
                    status: "",
                  });
                }}
                config={{
                  classes: BUTTON_FIELD_CLASSES,
                  showSubmitButton: true,
                  showResetButton: true,
                  submitButtonText: "Search",
                  resetButtonText: "Clear",
                  spacing: "small",
                }}
              />
            </div>
          }
          loading={isLoading}
          getRowClassName={() => "text-xs"}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default ManualSelectionTab;
